import React, { useState } from 'react';
import { X } from 'lucide-react';
import type { JobPosting } from '../../../types/hiring';
import {
  MAX_RESUME_BYTES,
  createManualJobApplication,
  isAllowedResumeFile,
} from '../../../utils/hiringFirestore';

type Props = {
  postings: JobPosting[];
  defaultPostingId?: string;
  onClose: () => void;
  onCreated: (applicationId: string) => void;
};

const AddCandidateModal: React.FC<Props> = ({
  postings,
  defaultPostingId,
  onClose,
  onCreated,
}) => {
  const [jobPostingId, setJobPostingId] = useState(defaultPostingId || postings[0]?.id || '');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [statement, setStatement] = useState('');
  const [resume, setResume] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    const posting = postings.find(p => p.id === jobPostingId);
    if (!posting) {
      setError('Select a job posting');
      return;
    }
    if (!firstName.trim() || !lastName.trim()) {
      setError('First and last name are required');
      return;
    }
    if (!resume) {
      setError('Resume is required');
      return;
    }
    if (!isAllowedResumeFile(resume)) {
      setError('Resume must be a PDF, DOC, or DOCX under 25 MB');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const id = await createManualJobApplication({
        posting,
        firstName,
        lastName,
        email,
        phone,
        statement,
        resume,
      });
      onCreated(id);
      onClose();
    } catch (e) {
      setError((e as Error).message || 'Failed to add candidate');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="people-hiring-modal-backdrop" onClick={onClose}>
      <div className="people-hiring-modal" onClick={e => e.stopPropagation()}>
        <div className="people-hiring-modal-header">
          <h3>Add candidate</h3>
          <button type="button" className="pm-btn pm-btn-ghost" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>
        <div className="people-hiring-modal-body">
          {error && <p style={{ color: '#b91c1c', margin: 0 }}>{error}</p>}
          <div className="people-hiring-field">
            <label>Job posting</label>
            <select value={jobPostingId} onChange={e => setJobPostingId(e.target.value)}>
              <option value="">Select a posting</option>
              {postings.map(p => (
                <option key={p.id} value={p.id}>
                  {p.title} ({p.status})
                </option>
              ))}
            </select>
          </div>
          <div className="people-hiring-row">
            <div className="people-hiring-field">
              <label>First name</label>
              <input value={firstName} onChange={e => setFirstName(e.target.value)} autoComplete="given-name" />
            </div>
            <div className="people-hiring-field">
              <label>Last name</label>
              <input value={lastName} onChange={e => setLastName(e.target.value)} autoComplete="family-name" />
            </div>
          </div>
          <div className="people-hiring-row">
            <div className="people-hiring-field">
              <label>Email (optional)</label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                autoComplete="email"
              />
            </div>
            <div className="people-hiring-field">
              <label>Phone (optional)</label>
              <input
                type="tel"
                value={phone}
                onChange={e => setPhone(e.target.value)}
                autoComplete="tel"
              />
            </div>
          </div>
          <div className="people-hiring-field">
            <label>Statement / notes (optional)</label>
            <textarea
              value={statement}
              onChange={e => setStatement(e.target.value)}
              placeholder="Cover letter, how you received the resume, or interview notes"
            />
          </div>
          <div className="people-hiring-field">
            <label>Resume</label>
            <input
              type="file"
              accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              onChange={e => setResume(e.target.files?.[0] || null)}
            />
            <p className="people-hiring-muted" style={{ margin: '6px 0 0' }}>
              PDF, DOC, or DOCX · max {Math.round(MAX_RESUME_BYTES / (1024 * 1024))} MB
              {resume ? ` · ${resume.name}` : ''}
            </p>
          </div>
          <div className="people-hiring-modal-footer">
            <button type="button" className="pm-btn pm-btn-ghost" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button
              type="button"
              className="pm-btn pm-btn-primary"
              onClick={() => void handleSave()}
              disabled={saving}
            >
              {saving ? 'Saving…' : 'Add candidate'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddCandidateModal;
